'use client';

import {Icon} from '@components/@icons';
import {AnimatedFormHelperText} from '@components/@shared';
import {
    FormControl,
    FormGroup,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import {unfocusActiveElement} from '@utils/unfocusActiveElement';
import classNames from 'classnames';
import {ForwardedRef, ReactNode, forwardRef} from 'react';
import {MeinScrollbarForMui} from '../Scrollbar/MeinScrollbarForMui';
import styles from './MeinSelect.module.css';

interface IProps<T> {
    options: readonly T[];
    value?: T | undefined;

    size?: 'small' | 'medium';
    variant?: 'outline' | 'solid';

    onChange: (value: T) => void;
    getOptionLabel?: (option: T) => string;
    getOptionValue?: (option: T) => string;

    className?: string;
    placeholder?: string;
    label?: string;

    helperText?: ReactNode;
    error?: boolean;
    disabled?: boolean;
}

export const MeinSelect = forwardRef(function MeinSelect<T>(
    {
        className,
        options,
        value,
        size = 'medium',
        variant = 'outline',
        helperText,
        error,
        onChange,
        getOptionValue = option => option as string,
        getOptionLabel = option => option as string,
        label,
        disabled,
        ...rest
    }: IProps<T>,
    ref: ForwardedRef<unknown>,
) {
    const getIsOptionSelected = (option: T) => {
        return value && getOptionValue(option) === getOptionValue(value);
    };

    const renderValue = (selectedValue?: ReturnType<typeof getOptionValue>) => {
        const option = options.find(o => getOptionValue(o) === selectedValue);
        return option ? getOptionLabel(option) : '';
    };

    const renderOption = (option: T) => {
        const isSelected = getIsOptionSelected(option);
        return (
            <MenuItem
                disableRipple
                className={styles.menuItem}
                key={getOptionValue(option)}
                value={getOptionValue(option)}
            >
                {isSelected ? (
                    <Icon name="circle-radio" className={styles.itemIcon} />
                ) : (
                    <Icon name="circle" className={styles.itemIcon} />
                )}
                {getOptionLabel(option)}
            </MenuItem>
        );
    };

    const handleChange = (
        e: SelectChangeEvent<ReturnType<typeof getOptionValue>>,
    ) => {
        const selectedValue = options.find(
            value => getOptionValue(value) === e.target.value,
        );
        onChange?.(selectedValue!);
    };

    return (
        <FormGroup>
            <FormControl>
                {variant === 'outline' && (
                    <InputLabel shrink className={styles.label}>
                        {label}
                    </InputLabel>
                )}
                <Select
                    disabled={disabled}
                    notched={variant === 'outline'}
                    label={label}
                    ref={ref}
                    onChange={handleChange}
                    value={value ? getOptionValue(value) : ''}
                    renderValue={renderValue}
                    className={classNames(
                        styles.select,
                        styles[variant],
                        styles[size],
                        className,
                    )}
                    onClose={unfocusActiveElement}
                    MenuProps={{
                        slotProps: {
                            paper: {component: MeinScrollbarForMui},
                        },
                        PopoverClasses: {
                            paper: styles.menu,
                        },
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    }}
                    IconComponent={() => (
                        <InputAdornment
                            className={styles.adornment}
                            position="end"
                        >
                            <Icon
                                name="chevron-down"
                                size={22}
                                className={styles.chevron}
                            />
                        </InputAdornment>
                    )}
                    {...rest}
                >
                    {options.map(renderOption)}
                </Select>
            </FormControl>
            <AnimatedFormHelperText error={error} helperText={helperText} />
        </FormGroup>
    );
});

export default MeinSelect as <T>(
    props: IProps<T> & {ref?: ForwardedRef<HTMLDivElement>},
) => ReturnType<typeof MeinSelect>;
